import { Link, NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { List, X, ArrowRight, Compass, CaretDown } from "@phosphor-icons/react";
import { SERVICES, BRAND } from "@/data/services";

const nav = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/process", label: "Process" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [mobileSvcOpen, setMobileSvcOpen] = useState(false);

  // Lock body scroll while mobile menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-zinc-200"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          className="group flex items-center gap-3"
          data-testid="brand-home-link"
        >
          <div
            className="w-11 h-11 border border-zinc-900 flex items-center justify-center bg-white group-hover:bg-zinc-900 transition-colors flex-shrink-0"
            aria-hidden="true"
          >
            <Compass
              size={24}
              weight="duotone"
              className="text-[#0044FF] group-hover:text-white transition-colors"
            />
          </div>
          <div className="leading-none inline-block">
            <div
              className="font-display font-semibold tracking-tight text-2xl sm:text-3xl text-zinc-950"
              data-testid="brand-wordmark"
            >
              {BRAND.name}
            </div>
            <div
              className="hidden sm:block font-mono text-[8.5px] uppercase text-zinc-500 mt-1.5 text-justify [text-align-last:justify] whitespace-nowrap"
              data-testid="brand-slogan"
            >
              Accurate Surveys · Detail-Driven CAD Support
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
          {nav.map((n) => (
            <div
              key={n.to}
              className="relative"
              onMouseEnter={() => n.label === "Services" && setSvcOpen(true)}
              onMouseLeave={() => n.label === "Services" && setSvcOpen(false)}
            >
              <NavLink
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? "text-zinc-950"
                      : "text-zinc-600 hover:text-zinc-950"
                  }`
                }
              >
                {n.label}
              </NavLink>
              {n.label === "Services" && svcOpen && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[640px]">
                  <div className="bg-white border border-zinc-200 shadow-xl p-4 grid grid-cols-2 gap-px bg-zinc-200">
                    {SERVICES.slice(0, 12).map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        className="bg-white p-3 hover:bg-zinc-50"
                        data-testid={`nav-service-${s.slug}`}
                      >
                        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">
                          {String(SERVICES.indexOf(s) + 1).padStart(2, "0")}
                        </div>
                        <div className="text-sm font-medium text-zinc-900 mt-0.5">
                          {s.title}
                        </div>
                      </Link>
                    ))}
                    <Link
                      to="/services"
                      className="bg-zinc-950 text-white p-3 flex items-center justify-between hover:bg-zinc-800"
                      data-testid="nav-all-services"
                    >
                      <span className="text-sm font-medium">
                        All 13 services
                      </span>
                      <ArrowRight size={16} weight="bold" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Link
            to="/free-trial"
            className="px-4 py-2 text-sm font-medium text-zinc-900 border border-zinc-900 hover:bg-zinc-900 hover:text-white btn-lift"
            data-testid="cta-free-trial-header"
          >
            Free Trial
          </Link>
          <Link
            to="/contact"
            className="px-4 py-2 text-sm font-medium text-white bg-[#0044FF] hover:bg-[#0033CC] btn-lift inline-flex items-center gap-2"
            data-testid="cta-get-started-header"
          >
            Get Started <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        <button
          className="lg:hidden p-2"
          aria-label="Toggle menu"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden fixed inset-0 top-0 z-50 bg-white flex flex-col"
          data-testid="mobile-menu-panel"
        >
          {/* Top bar of mobile drawer */}
          <div className="flex items-center justify-between px-6 h-20 border-b border-zinc-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 border border-zinc-900 flex items-center justify-center bg-white">
                <Compass size={24} weight="duotone" className="text-[#0044FF]" />
              </div>
              <div className="leading-none">
                <div className="font-display font-semibold text-2xl tracking-tight text-zinc-950">
                  {BRAND.name}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500 mt-1">
                  Accurate Surveys · Detail-Driven CAD Support
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-2 -mr-2"
              aria-label="Close menu"
              data-testid="mobile-menu-close"
            >
              <X size={24} />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">
            <nav className="px-6 pt-4 pb-2" aria-label="Mobile primary">
              {nav.map((n) =>
                n.label === "Services" ? (
                  <div key={n.to} className="border-b border-zinc-100">
                    <button
                      type="button"
                      onClick={() => setMobileSvcOpen((x) => !x)}
                      className="w-full flex items-center justify-between px-1 py-4 text-lg font-display tracking-tight text-zinc-950"
                      data-testid="mobile-services-toggle"
                      aria-expanded={mobileSvcOpen}
                    >
                      <span>Services</span>
                      <CaretDown
                        size={16}
                        weight="bold"
                        className={`transition-transform ${mobileSvcOpen ? "rotate-180 text-[#0044FF]" : "text-zinc-400"}`}
                      />
                    </button>
                    {mobileSvcOpen && (
                      <div
                        className="pb-4 pt-1 -mx-6 px-6 bg-zinc-50 border-y border-zinc-100"
                        data-testid="mobile-services-dropdown"
                      >
                        <NavLink
                          to="/services"
                          onClick={() => {
                            setOpen(false);
                            setMobileSvcOpen(false);
                          }}
                          className="flex items-center justify-between py-3 text-sm font-medium text-[#0044FF]"
                          data-testid="mobile-service-all"
                        >
                          View all 13 services
                          <ArrowRight size={14} weight="bold" />
                        </NavLink>
                        <ul className="divide-y divide-zinc-200 border-t border-zinc-200">
                          {SERVICES.map((s, i) => (
                            <li key={s.slug}>
                              <NavLink
                                to={`/services/${s.slug}`}
                                onClick={() => {
                                  setOpen(false);
                                  setMobileSvcOpen(false);
                                }}
                                className="flex items-center justify-between gap-3 py-3 text-sm text-zinc-800 hover:text-[#0044FF]"
                                data-testid={`mobile-service-${s.slug}`}
                              >
                                <span className="flex items-center gap-3">
                                  <span className="font-mono text-[10px] text-zinc-400 w-5">
                                    {String(i + 1).padStart(2, "0")}
                                  </span>
                                  {s.title}
                                </span>
                                <ArrowRight size={13} weight="bold" className="text-zinc-300" />
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block border-b border-zinc-100 px-1 py-4 text-lg font-display tracking-tight ${
                        isActive ? "text-[#0044FF]" : "text-zinc-950 hover:text-[#0044FF]"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                ),
              )}
            </nav>

            {/* Contact emails */}
            <div className="px-6 py-6 mt-2 border-t border-zinc-200 bg-zinc-50">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-3">
                Reach us directly
              </div>
              <ul className="space-y-2.5 text-sm font-mono">
                <li>
                  <Link
                    to="/contact"
                    onClick={() => setOpen(false)}
                    className="text-zinc-900 hover:text-[#0044FF] break-all"
                  >
                    {BRAND.email}
                  </Link>
                </li>
                <li>
                  <a href={BRAND.tel_url} className="text-zinc-900 hover:text-[#0044FF]">
                    {BRAND.phone_display}
                  </a>
                </li>
                <li>
                  <a
                    href={BRAND.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-900 hover:text-[#25D366] inline-flex items-center gap-1.5"
                  >
                    <span className="inline-block w-2 h-2 bg-[#25D366] rounded-full" />
                    WhatsApp
                  </a>
                </li>
              </ul>
              <div className="mt-5 pt-4 border-t border-zinc-200 text-[11px] text-zinc-600 leading-relaxed">
                <span className="font-mono uppercase text-[9px] tracking-[0.2em] text-amber-700">Note</span>
                <br />
                CAD drafting services only. We do not perform field surveys.
              </div>
            </div>
          </div>

          {/* Sticky CTAs */}
          <div className="px-6 py-4 border-t border-zinc-200 bg-white flex-shrink-0 flex gap-3">
            <Link
              to="/free-trial"
              onClick={() => setOpen(false)}
              className="flex-1 py-3 text-sm font-medium text-zinc-900 border border-zinc-900 text-center btn-lift"
              data-testid="mobile-cta-free-trial"
            >
              Free Trial
            </Link>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="flex-1 py-3 text-sm font-medium text-white bg-[#0044FF] text-center btn-lift inline-flex items-center justify-center gap-2"
              data-testid="mobile-cta-get-started"
            >
              Get Started <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
